import React, { useEffect, useMemo, useState } from "react";
import {
  Heart,
  MoreHorizontal,
  Pencil,
  Trash2,
  CornerDownRight,
  Send,
  X,
  MessageCircle,
  Smile,
} from "lucide-react";
import API from "../../../api";
import "./comment.css";


// =========================================================
// HELPERS
// =========================================================

const formatTime = (date) => {
  if (!date) return "";

  const now = new Date();
  const created = new Date(date);

  const diff = Math.floor((now - created) / 1000);

  if (diff < 10) return "now";
  if (diff < 60) return `${diff}s`;

  const minutes = Math.floor(diff / 60);
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;

  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w`;

  return created.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year:
      created.getFullYear() !== now.getFullYear()
        ? "numeric"
        : undefined,
  });
};


// =========================================================
// AVATAR
// =========================================================

function UserAvatar({ user, size = "normal" }) {
  const [imageError, setImageError] = useState(false);

  const name = user?.name || "User";
  const photo = user?.profilePhoto;

  const initial = name.charAt(0).toUpperCase();

  return (
    <div className={`comment-user-avatar ${size}`}>
      {photo && !imageError ? (
        <img
          src={photo}
          alt={name}
          onError={() => setImageError(true)}
        />
      ) : (
        <span>{initial}</span>
      )}
    </div>
  );
}


// =========================================================
// SINGLE COMMENT
// =========================================================

function CommentItem({
  comment,
  replies,
  currentUser,
  onLike,
  onReply,
  onEdit,
  onDelete,
}) {
  const [showReplies, setShowReplies] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  const isOwner =
    currentUser?._id?.toString() ===
    comment.user?._id?.toString();

  const replyCount = replies?.length || 0;

  return (
    <div className="comment-thread">

      <div className="comment-item">

        <UserAvatar user={comment.user} />

        <div className="comment-main">

          {/* USER + MENU */}
          <div className="comment-top-row">

            <div className="comment-user-name">
              {comment.user?.name || "User"}
            </div>

            <div className="comment-menu-wrapper">

              <button
                className="comment-more-btn"
                onClick={() => setShowMenu(!showMenu)}
                aria-label="Comment options"
              >
                <MoreHorizontal size={18} />
              </button>

              {showMenu && (
                <div className="comment-menu">

                  {isOwner && (
                    <>
                      <button
                        onClick={() => {
                          setShowMenu(false);
                          onEdit(comment);
                        }}
                      >
                        <Pencil size={15} />
                        Edit
                      </button>

                      <button
                        className="danger"
                        onClick={() => {
                          setShowMenu(false);
                          onDelete(comment);
                        }}
                      >
                        <Trash2 size={15} />
                        Delete
                      </button>
                    </>
                  )}

                  {!isOwner && (
                    <button
                      onClick={() => setShowMenu(false)}
                    >
                      Report
                    </button>
                  )}

                </div>
              )}

            </div>

          </div>


          {/* COMMENT TEXT */}

          <div className="comment-text">
            {comment.text}
          </div>


          {/* META + ACTIONS */}

          <div className="comment-action-row">

            <span className="comment-time">
              {formatTime(comment.createdAt)}
            </span>

            {comment.likesCount > 0 && (
              <span className="comment-like-count">
                {comment.likesCount}{" "}
                {comment.likesCount === 1 ? "like" : "likes"}
              </span>
            )}

            <button
              className="comment-action-btn"
              onClick={() => onReply(comment)}
            >
              Reply
            </button>

          </div>


          {/* REPLIES */}

          {replyCount > 0 && (
            <button
              className="view-replies-btn"
              onClick={() => setShowReplies(!showReplies)}
            >
              <span className="reply-line" />
              {showReplies
                ? "Hide replies"
                : `View ${replyCount} ${
                    replyCount === 1 ? "reply" : "replies"
                  }`}
            </button>
          )}

          {showReplies && replyCount > 0 && (
            <div className="replies-container">

              {replies.map((reply) => (
                <ReplyItem
                  key={reply._id}
                  comment={reply}
                  currentUser={currentUser}
                  onLike={onLike}
                  onReply={onReply}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ))}

            </div>
          )}

        </div>


        {/* LIKE */}

        <button
          className={`comment-heart-btn ${
            comment.likedByMe ? "liked" : ""
          }`}
          onClick={() => onLike(comment)}
          aria-label="Like comment"
        >
          <Heart
            size={17}
            fill={comment.likedByMe ? "currentColor" : "none"}
            strokeWidth={2}
          />
        </button>

      </div>

    </div>
  );
}


// =========================================================
// REPLY ITEM
// =========================================================

function ReplyItem({
  comment,
  currentUser,
  onLike,
  onReply,
  onEdit,
  onDelete,
}) {
  const [showMenu, setShowMenu] = useState(false);

  const isOwner =
    currentUser?._id?.toString() ===
    comment.user?._id?.toString();

  return (
    <div className="reply-item">

      <UserAvatar user={comment.user} size="small" />

      <div className="reply-main">

        <div className="reply-top-row">

          <strong>
            {comment.user?.name || "User"}
          </strong>

          <div className="comment-menu-wrapper">

            <button
              className="comment-more-btn small"
              onClick={() => setShowMenu(!showMenu)}
            >
              <MoreHorizontal size={16} />
            </button>

            {showMenu && (
              <div className="comment-menu">

                {isOwner && (
                  <>
                    <button
                      onClick={() => {
                        setShowMenu(false);
                        onEdit(comment);
                      }}
                    >
                      <Pencil size={14} />
                      Edit
                    </button>

                    <button
                      className="danger"
                      onClick={() => {
                        setShowMenu(false);
                        onDelete(comment);
                      }}
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  </>
                )}

                {!isOwner && (
                  <button
                    onClick={() => setShowMenu(false)}
                  >
                    Report
                  </button>
                )}

              </div>
            )}

          </div>

        </div>

        <div className="reply-text">
          {comment.text}
        </div>

        <div className="comment-action-row">

          <span className="comment-time">
            {formatTime(comment.createdAt)}
          </span>

          {comment.likesCount > 0 && (
            <span className="comment-like-count">
              {comment.likesCount} likes
            </span>
          )}

          <button
            className="comment-action-btn"
            onClick={() => onReply(comment)}
          >
            Reply
          </button>

        </div>

      </div>

      <button
        className={`comment-heart-btn reply-heart ${
          comment.likedByMe ? "liked" : ""
        }`}
        onClick={() => onLike(comment)}
      >
        <Heart
          size={15}
          fill={comment.likedByMe ? "currentColor" : "none"}
        />
      </button>

    </div>
  );
}


// =========================================================
// MAIN MODAL
// =========================================================

export default function CommentsModal({
  post,
  onClose,
}) {

  const [comments, setComments] = useState([]);
  const [replies, setReplies] = useState([]);

  const [text, setText] = useState("");

  const [replyingTo, setReplyingTo] = useState(null);

  const [editingComment, setEditingComment] = useState(null);

  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const currentUser = useMemo(() => {
    try {
      return JSON.parse(
        localStorage.getItem("civilink_user")
      );
    } catch {
      return null;
    }
  }, []);


  // =======================================================
  // LOAD
  // =======================================================

  useEffect(() => {
    loadComments();
  }, [post?._id]);


  const loadComments = async () => {

    if (!post?._id) return;

    try {

      setLoading(true);

      const res = await API.get(
        `/comments/${post._id}`
      );

      setComments(res.data.comments || []);
      setReplies(res.data.replies || []);

    } catch (err) {

      console.error(
        "Load comments error:",
        err
      );

    } finally {

      setLoading(false);

    }
  };


  // =======================================================
  // REPLY GROUPING
  // =======================================================

  const repliesByParent = useMemo(() => {

    const grouped = {};

    replies.forEach((reply) => {

      const parentId =
        reply.parentComment?._id ||
        reply.parentComment;

      if (!parentId) return;

      if (!grouped[parentId]) {
        grouped[parentId] = [];
      }

      grouped[parentId].push(reply);

    });

    return grouped;

  }, [replies]);


  // =======================================================
  // SUBMIT
  // =======================================================

  const submitComment = async () => {

    const cleanText = text.trim();

    if (!cleanText || sending) return;

    try {

      setSending(true);

      // EDIT
      if (editingComment) {

        const res = await API.put(
          `/comments/${editingComment._id}`,
          {
            text: cleanText,
          }
        );

        const updated = res.data;

        setComments((prev) =>
          prev.map((c) =>
            c._id === updated._id
              ? {
                  ...c,
                  ...updated,
                }
              : c
          )
        );

        setReplies((prev) =>
          prev.map((c) =>
            c._id === updated._id
              ? {
                  ...c,
                  ...updated,
                }
              : c
          )
        );

        setEditingComment(null);
        setText("");

        return;
      }


      // ADD COMMENT / REPLY

      const payload = {
        text: cleanText,
      };

      if (replyingTo) {
        payload.parentComment =
          replyingTo._id;
      }

      const res = await API.post(
        `/comments/${post._id}`,
        payload
      );

      const newComment = res.data;

      if (replyingTo) {

        setReplies((prev) => [
          ...prev,
          newComment,
        ]);

      } else {

        setComments((prev) => [
          newComment,
          ...prev,
        ]);

      }

      setText("");
      setReplyingTo(null);

    } catch (err) {

      console.error(
        "Submit comment error:",
        err
      );

    } finally {

      setSending(false);

    }
  };


  // =======================================================
  // LIKE
  // =======================================================

  const likeComment = async (comment) => {

    try {

      const res = await API.post(
        `/comments/like/${comment._id}`
      );

      const updateComment = (item) => {

        if (item._id !== comment._id) {
          return item;
        }

        return {
          ...item,
          likesCount: res.data.likesCount,
          likedByMe: res.data.likedByMe,
        };

      };

      setComments((prev) =>
        prev.map(updateComment)
      );

      setReplies((prev) =>
        prev.map(updateComment)
      );

    } catch (err) {

      console.error(
        "Like comment error:",
        err
      );

    }
  };


  // =======================================================
  // EDIT
  // =======================================================

  const editComment = (comment) => {

    setEditingComment(comment);
    setReplyingTo(null);
    setText(comment.text);

  };


  // =======================================================
  // DELETE
  // =======================================================

  const deleteComment = async (comment) => {

    const confirmed = window.confirm(
      "Delete this comment?"
    );

    if (!confirmed) return;

    try {

      await API.delete(
        `/comments/${comment._id}`
      );

      setComments((prev) =>
        prev.filter(
          (c) => c._id !== comment._id
        )
      );

      setReplies((prev) =>
        prev.filter(
          (c) =>
            c._id !== comment._id &&
            c.parentComment !== comment._id &&
            c.parentComment?._id !== comment._id
        )
      );

    } catch (err) {

      console.error(
        "Delete comment error:",
        err
      );

    }
  };


  // =======================================================
  // REPLY
  // =======================================================

  const startReply = (comment) => {

    setReplyingTo(comment);
    setEditingComment(null);

    setText(
      `@${comment.user?.name || "user"} `
    );

  };


  // =======================================================
  // CANCEL EDIT / REPLY
  // =======================================================

  const cancelComposerMode = () => {

    setReplyingTo(null);
    setEditingComment(null);
    setText("");

  };


  // =======================================================
  // KEYBOARD
  // =======================================================

  const handleKeyDown = (e) => {

    if (
      e.key === "Enter" &&
      !e.shiftKey
    ) {

      e.preventDefault();
      submitComment();

    }

  };


  const totalComments =
    comments.length + replies.length;


  // =======================================================
  // UI
  // =======================================================

  return (

    <div
      className="comments-backdrop"
      onClick={onClose}
    >

      <div
        className="comments-modal"
        onClick={(e) =>
          e.stopPropagation()
        }
      >

        {/* =================================================
            HEADER
        ================================================= */}

        <div className="comments-header">

          <div>

            <h2>
              Comments
            </h2>

            <span>
              {totalComments}{" "}
              {totalComments === 1
                ? "comment"
                : "comments"}
            </span>

          </div>

          <button
            className="comments-close-btn"
            onClick={onClose}
            aria-label="Close comments"
          >
            <X size={21} />
          </button>

        </div>


        {/* =================================================
            CONTENT
        ================================================= */}

        <div className="comments-content">

          {loading ? (

            <div className="comments-loading">

              <div className="comment-loader" />
              <div className="comment-loader" />
              <div className="comment-loader" />

            </div>

          ) : comments.length === 0 ? (

            <div className="comments-empty">

              <div className="comments-empty-icon">
                <MessageCircle size={30} />
              </div>

              <h3>
                No comments yet
              </h3>

              <p>
                Be the first to share your thoughts.
              </p>

            </div>

          ) : (

            <div className="comments-list">

              {comments.map((comment) => (

                <CommentItem
                  key={comment._id}
                  comment={comment}
                  replies={
                    repliesByParent[
                      comment._id
                    ] || []
                  }
                  currentUser={currentUser}
                  onLike={likeComment}
                  onReply={startReply}
                  onEdit={editComment}
                  onDelete={deleteComment}
                />

              ))}

            </div>

          )}

        </div>


        {/* =================================================
            COMPOSER
        ================================================= */}

        <div className="comment-composer">

          {(replyingTo || editingComment) && (

            <div className="composer-context">

              <div>

                {editingComment
                  ? "Editing comment"
                  : `Replying to ${
                      replyingTo?.user?.name ||
                      "user"
                    }`}

              </div>

              <button
                onClick={cancelComposerMode}
              >
                <X size={14} />
              </button>

            </div>

          )}


          <div className="composer-row">

            <UserAvatar
              user={currentUser}
              size="composer"
            />

            <div className="composer-input-wrap">

              <textarea
                value={text}
                onChange={(e) => {

                  if (
                    e.target.value.length <=
                    500
                  ) {
                    setText(e.target.value);
                  }

                }}
                onKeyDown={handleKeyDown}
                placeholder={
                  replyingTo
                    ? "Write a reply..."
                    : editingComment
                    ? "Edit your comment..."
                    : "Add a comment..."
                }
                rows={1}
              />

              <div className="composer-bottom">

                <div className="composer-tools">

                  <Smile size={17} />

                  <span>
                    {text.length}/500
                  </span>

                </div>

                <button
                  className={`send-comment-btn ${
                    text.trim()
                      ? "active"
                      : ""
                  }`}
                  onClick={submitComment}
                  disabled={
                    !text.trim() ||
                    sending
                  }
                  aria-label="Send comment"
                >
                  <Send size={17} />
                </button>

              </div>

            </div>

          </div>

        </div>

      </div>

    </div>

  );
}